import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { Autocomplete, capitalize, TextField, Typography } from "@mui/material";
import { Autotractor } from "protos/atk/autotractor";
import { FarmsByIdType } from "./JobList";
import { State } from "protos/rtc/jobs";
import { stateToLocaleString } from "common/utils/enumTranslations";
import { useTranslation } from "react-i18next";
import { Zone } from "protos/farm/farm";
import React, { FC } from "react";

const allStates = [
  State.ACKNOWLEDGED,
  State.CANCELLED,
  State.COMPLETED,
  State.FAILED,
  State.IN_PROGRESS,
  State.NEW,
  State.PAUSED,
  State.PENDING,
  State.READY,
];

interface JobFiltersProps {
  rigs: Autotractor[];
  setRigs: (rigs: Autotractor[]) => void;
  fields: Zone[];
  setFields: (fields: Zone[]) => void;
  states: State[];
  setStates: (states: State[]) => void;
  farmsById: FarmsByIdType;
}
export const JobFilters: FC<JobFiltersProps> = ({
  rigs,
  setRigs,
  fields,
  setFields,
  states,
  setStates,
  farmsById,
}) => {
  const { t } = useTranslation();
  const { data: rigsResp } = atkEquipmentApi.endpoints.searchRigs.useQuery({});
  const allFields = Object.values(farmsById).flatMap((f) =>
    Object.values(f.zonesById)
  );

  return (
    <div className="w-full flex flex-col p-1 rounded-md">
      <div className="flex justify-between gap-2 min-h-16">
        <Autocomplete
          multiple
          fullWidth
          options={rigsResp?.rigs ?? []}
          value={rigs}
          onChange={(_, r) => setRigs(r)}
          renderOption={(props, r) => (
            <li {...props} key={`${r.id}-${r.customerProvidedName}`}>
              <div className="flex flex-col mx-auto text-center">
                <Typography className="text-sm font-bold">
                  {r.customerProvidedName}
                </Typography>
                <div className="flex gap-2">
                  {r.tractor?.name && (
                    <Typography color="textSecondary" className="text-sm">
                      {r.tractor.name}
                    </Typography>
                  )}
                  {r.implements.at(0) && (
                    <Typography color="textSecondary" className="text-sm">
                      {r.implements.at(0)?.name}
                    </Typography>
                  )}
                </div>
              </div>
            </li>
          )}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase();
            return options.filter(
              (r) =>
                r.customerProvidedName.toLowerCase().includes(query) ||
                r.tractor?.name.toLowerCase().includes(query) ||
                r.implements.at(0)?.name.toLowerCase().includes(query) ||
                r.serial.toLowerCase().includes(query)
            );
          }}
          getOptionLabel={(option) => option.customerProvidedName}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("utils.actions.searchLong", {
                subject: t("models.equipment.rig_other"),
              })}
            />
          )}
        />
        <Autocomplete
          multiple
          fullWidth
          options={allStates}
          value={states}
          onChange={(_, s) => setStates(s)}
          renderOption={(props, s) => (
            <li {...props}>
              <Typography className="text-sm">
                {capitalize(stateToLocaleString(t, s))}
              </Typography>
            </li>
          )}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase();
            return options.filter((s) =>
              stateToLocaleString(t, s).toLowerCase().includes(query)
            );
          }}
          getOptionLabel={(option) =>
            capitalize(stateToLocaleString(t, option))
          }
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("utils.actions.searchLong", {
                subject: t("models.autotractor.states.state_other"),
              })}
            />
          )}
        />
        <Autocomplete
          multiple
          fullWidth
          options={allFields}
          value={fields}
          onChange={(_, f) => setFields(f)}
          renderOption={(props, f) => (
            <li {...props} key={`${f.id?.id}-${f.name}`}>
              <Typography className="text-sm">{f.name}</Typography>
            </li>
          )}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase();
            return options.filter((f) => f.name.toLowerCase().includes(query));
          }}
          getOptionLabel={(option) => option.name}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("utils.actions.searchLong", {
                subject: t("views.farms.zoneTypes.field_other"),
              })}
            />
          )}
        />
      </div>
    </div>
  );
};
